import React, { Fragment, useEffect, useState, useRef, useContext } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Globe, LogIn, Layers } from 'react-feather';
import { Btn, H3, H4, P, Badges } from "../../../AbstractElements";
import { useForm } from 'react-hook-form';
import { Row, Col, Card, CardHeader, CardBody, CardFooter, Form, FormGroup, Label } from 'reactstrap'
import TableBuilder, { RegisteredInputsBuilder, ApiRequestBuilder, ToastBuilder, ModalButtonBuilder, FormOptionsBuilder, ButtonBuilder } from '../../../GlobalComponents/Builders';
import CreatableMultiSelect from '../../../GlobalComponents/Common/Component/CreatableMultiSelect';
import { setInitialFormValues, renameKeysCamelToSnake, buildOptionsFromArray, setUpdateValues, parseJSONSafe } from '../../../GlobalComponents/Helpers';
import { AccountContext } from '../../../GlobalComponents/Authentication/Accounts';
import { ContentLoader } from '../../../Layout/Loader';
import moment from 'moment';

const Partner = () => {

    const { userSession, emulatePartnerPortal } = useContext(AccountContext);

    const { partnerId } = useParams();
    const navigate = useNavigate();

    const [partnerData, setPartnerData] = useState(null);
    const [toolData, setToolData] = useState([]);
    const [toolsToOmit, setToolsToOmit] = useState([])
    const [subscriptionData, setSubscriptionData] = useState([]);
    const [altDomains, setAltDomains] = useState([]);
    const [isProcessing, setIsProcessing] = useState(true);

    const emailsRef = useRef()

    const { register, handleSubmit, setValue, reset, getValues, formState: { errors } } = useForm();

    // altDomains is a pill/tag input (CreatableMultiSelect) rather than a
    // RegisteredInputsBuilder field, so register it manually with react-hook-form.
    // The form value is kept as a JSON string so it matches the stored column
    // format and the setUpdateValues() diff.
    useEffect(() => {
        register('altDomains');
    }, [register]);

    // Normalize alt_domains (JSON array, or legacy comma string) into an array.
    const parseAltDomains = (raw) => {
        if (!raw) return [];
        const parsed = parseJSONSafe(raw);
        if (Array.isArray(parsed)) return parsed.map(d => String(d).trim()).filter(Boolean);
        // Legacy fallback: comma-delimited or single bare domain
        return String(raw).split(',').map(d => d.trim()).filter(Boolean);
    };

    const handleAltDomainsChange = (values) => {
        setAltDomains(values);
        setValue('altDomains', JSON.stringify(values));
    };

    // Validate a hostname/domain: labels of letters, digits and hyphens
    // (not starting/ending with a hyphen), separated by dots, ending in a TLD
    // of at least two letters. Strips an optional protocol and any path first,
    // so pasting "https://sub.example.com/path" still validates the host.
    const isValidDomain = (raw) => {
        if (!raw) return false;
        const host = String(raw)
            .trim()
            .replace(/^[a-z]+:\/\//i, '')  // drop protocol
            .replace(/[/?#].*$/, '')       // drop path/query/hash
            .replace(/\.$/, '');           // drop trailing dot
        if (host.length > 253) return false;
        const domainPattern = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
        return domainPattern.test(host);
    };

    const handleInvalidDomains = (rejected) => {
        ToastBuilder('warn', `Not a valid domain: ${rejected.join(', ')}`);
    };

    const handleSubscriptionCreate = (formData) => {
        const toolSelected = toolData.find(tool => tool.tool_id === parseInt(formData.toolToSubscribe))
        const payload = { admin: userSession.name, partnerId: partnerId, partnerName: partnerData.name, toolName: toolSelected.name, toolId: toolSelected.tool_id }
        ApiRequestBuilder('toolsToolIdSubscriptionsPost', { toolId: formData.toolToSubscribe }, { ...payload })
            .then(function (results) {
                getPartnerData()
            }).catch((err) => { })
    }

    const onProfileEdit = (profileInformation) => {
        const profileKeys = ['isActive', 'name', 'phoneNumber', 'domain', 'altDomains', 'accountingClientId']
        for (let key in profileInformation) {
            if (!(profileKeys.includes(key))) {
                delete profileInformation[key]
            }
        }

        const dataToUpdate = setUpdateValues(profileInformation, partnerData, 'camel', ['partnerId', 'subscriptions'])

        if (Object.keys(dataToUpdate).length > 0) {
            profileInformation = renameKeysCamelToSnake(profileInformation)
            ApiRequestBuilder('partnersPartnerIdPatch', { partnerId }, { ...profileInformation }).then(function (results) {
                ToastBuilder('success', 'Partner Profile Updated!')
                reset();
                getPartnerData();
            }).catch((err) => {
                ToastBuilder('error', 'Something went wrong')
            })
        } else {
            ToastBuilder('info', 'Nothing to update')
        }
    }

    const getPartnerData = () => {
        ApiRequestBuilder('partnersPartnerIdGet', { partnerId }, null, { setIsProcessing })
            .then(function (results) {
                if (!results.data.partner_id) window.location.href = process.env.PUBLIC_URL + '/partners'
                else {
                    setInitialFormValues(results.data, setValue)
                    // Seed accountingClientId to a controlled '' when the API returns null, so
                    // clearing the field and saving sends '' (a real clear) and round-trips.
                    setValue('accountingClientId', results.data.accounting_client_id ?? '')
                    setPartnerData(results.data)
                    setToolsToOmit(results.data.subscriptions.filter(sub => sub.is_active === 1).map(element => element.tool_id))
                    setSubscriptionData(results.data.subscriptions.filter(sub => sub.is_active === 1))

                    // Seed the pill input and keep the form value as a JSON string.
                    const parsedAltDomains = parseAltDomains(results.data.alt_domains)
                    setAltDomains(parsedAltDomains)
                    setValue('altDomains', JSON.stringify(parsedAltDomains))
                }
            }).catch((err) => { })
    }

    const getToolsData = () => {
        ApiRequestBuilder('toolsGet').then(function (results) {
            if (results.data) {
                const filteredTools = results.data.filter(element => !element.partner_id || element.partner_id === partnerId)
                setToolData(filteredTools)
            }
        }).catch((err) => { })
    }

    const sendEmail = (formData) => {
        if (!stringToListHandler(formData.userEmails)) {
            ToastBuilder('info', "No email recipients.")
        } else if (subscriptionData.length === 0) {
            ToastBuilder('info', "No active subscriptions.")
        }
        else {
            ApiRequestBuilder('partnersPartnerIdOnboardPost', { partnerId }, { ...formData, emails: emailsRef.current }, { toastOnSuccess: 'Emails sent.' }).then(function (results) {
            }).catch((err) => {
                ToastBuilder('success', 'Something went wrong.')
            })
        }
    }

    const stringToListHandler = (string) => {
        let set = new Set(string.replace(/\s/g, '').split(','));
        set.delete('')
        // This is where the email validation will be added for V1+
        emailsRef.current = [...set]
        if (set.size > 0) return true
        return false
    }

    useEffect(() => {
        document.title = 'View Partner'
        getToolsData()
        getPartnerData()
    }, []);

    const partnerName = partnerData && partnerData.name && partnerData.name !== '' ? partnerData.name : 'Partner'
    const partnerInitial = partnerName.charAt(0).toUpperCase()
    const isActive = partnerData && partnerData.is_active === 1

    return (
        <Fragment>
            <ContentLoader isLoading={isProcessing} />
            {partnerData ?
                <div className="edit-profile">
                    {/* ---- Hero / page header ---- */}
                    <Card className="mb-4">
                        <CardBody>
                            <Row className="align-items-center">
                                <Col md="8">
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
                                            style={{ width: 64, height: 64, fontSize: 28, fontWeight: 600, flex: '0 0 64px' }}
                                        >
                                            {partnerInitial}
                                        </div>
                                        <div className="ms-3">
                                            <div className="d-flex align-items-center flex-wrap">
                                                <H3 attrH3={{ className: 'mb-0 me-2' }}>{partnerName}</H3>
                                                <Badges attrBadge={{ color: isActive ? 'success' : 'secondary', pill: true }}>
                                                    {isActive ? 'Active' : 'Inactive'}
                                                </Badges>
                                            </div>
                                            {partnerData.domain ?
                                                <P attrPara={{ className: 'mb-0 mt-1 txt-secondary d-flex align-items-center' }}>
                                                    <Globe size={14} style={{ marginRight: 6 }} />{partnerData.domain}
                                                </P>
                                                : null}
                                        </div>
                                    </div>
                                </Col>
                                <Col md="4" className="text-md-end mt-3 mt-md-0">
                                    <ButtonBuilder
                                        label={<span><LogIn size={16} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />Enter Partner Portal</span>}
                                        className='btn-primary m-1'
                                        useSpinner={false}
                                        onClick={() => emulatePartnerPortal(partnerId, partnerData.name)}
                                    />
                                    <ButtonBuilder
                                        label={<span><ArrowLeft size={16} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />Back</span>}
                                        className='btn-outline-primary m-1'
                                        useSpinner={false}
                                        onClick={() => navigate(`${process.env.PUBLIC_URL}/partners`)}
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    <Row>
                        {/* ---- Profile column ---- */}
                        <Col xl="6">
                            <Form onSubmit={handleSubmit(onProfileEdit)}>
                                <Card>
                                    <CardHeader className="pb-0">
                                        <H4 attrH4={{ className: 'mb-1' }}>Company Profile</H4>
                                        <P attrPara={{ className: 'mb-0 f-14' }}>
                                            Manage this partner's identity and contact details.
                                        </P>
                                    </CardHeader>
                                    <CardBody>
                                        <RegisteredInputsBuilder
                                            registrator={register}
                                            registratorErrors={errors}
                                            inputs={[
                                                {
                                                    label: 'Company Name',
                                                    placeholder: 'Company ABC',
                                                    size: 12,
                                                    name: 'name'
                                                },
                                                {
                                                    label: 'Phone Number',
                                                    placeholder: 'Please enter phone number',
                                                    size: 12,
                                                    name: 'phoneNumber'
                                                },
                                                {
                                                    label: 'Domain Name',
                                                    placeholder: 'company-site.com',
                                                    size: 12,
                                                    name: 'domain'
                                                },
                                                {
                                                    label: 'Accounting Client ID',
                                                    placeholder: 'Billing system client reference',
                                                    size: 12,
                                                    name: 'accountingClientId'
                                                }
                                            ]}
                                        />
                                        <FormGroup>
                                            <Label>Alt Domains</Label>
                                            <CreatableMultiSelect
                                                value={altDomains}
                                                onChange={handleAltDomainsChange}
                                                autoNumeric={false}
                                                size='md'
                                                splitOnWhitespace={true}
                                                validateValue={isValidDomain}
                                                onInvalid={handleInvalidDomains}
                                                placeholder='Type a domain and press Enter'
                                            />
                                            <span className="f-12">
                                                Type a domain and press Enter (or Tab, comma, or space). Paste supported — space, comma, tab, or newline separated.
                                            </span>
                                        </FormGroup>
                                        {/* Read-only details: never added to profileKeys, never PATCHed. */}
                                        <FormGroup>
                                            <Label>Primary contact</Label>
                                            <P attrPara={{ className: 'mb-0' }}>
                                                {partnerData.primary_contact && partnerData.primary_contact !== ''
                                                    ? partnerData.primary_contact
                                                    : '—'}
                                            </P>
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>Last activity</Label>
                                            <P attrPara={{ className: 'mb-0' }}>
                                                {partnerData.last_activity_at
                                                    ? moment(new Date(partnerData.last_activity_at * 1000)).format('YYYY-MM-DD')
                                                    : 'No activity'}
                                            </P>
                                        </FormGroup>
                                    </CardBody>
                                    <CardFooter className="text-end">
                                        <Btn attrBtn={{ color: "primary", type: "submit" }} >{'Update Profile'}</Btn>
                                    </CardFooter>
                                </Card>
                            </Form>
                        </Col>

                        {/* ---- Admin + subscriptions column ---- */}
                        <Col xl="6">
                            <Card>
                                <CardHeader className="pb-0">
                                    <H4 attrH4={{ className: 'mb-1' }}>Admin</H4>
                                    <P attrPara={{ className: 'mb-0 f-14' }}>
                                        Send onboarding invitations to portal users.
                                    </P>
                                </CardHeader>
                                <CardBody>
                                    <ModalButtonBuilder title='Send Onboarding Email'
                                        className='btn-primary' size='lg'
                                        formId='send-onboarding-email-form'
                                        body={
                                            <Form
                                                id='send-onboarding-email-form'
                                                onSubmit={handleSubmit(sendEmail)}
                                            >
                                                <RegisteredInputsBuilder
                                                    registrator={register}
                                                    registratorErrors={errors}
                                                    inputs={[
                                                        {
                                                            label: 'User Emails',
                                                            size: 12,
                                                            labelSize: 3,
                                                            type: 'text',
                                                            placeholder: 'portal_user@gmail.com, company@email.com',
                                                            name: 'userEmails',
                                                            helpText: 'Please enter Comma separated email addresses',
                                                            onKeyDown: (event) => { if (event.code === 'Enter') event.preventDefault() }
                                                        },
                                                    ]} />
                                            </Form>
                                        } />
                                </CardBody>
                            </Card>
                            <Card>
                                <CardHeader className="pb-0">
                                    <H4 attrH4={{ className: 'mb-1' }}>
                                        <Layers size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />Subscriptions
                                    </H4>
                                    <P attrPara={{ className: 'mb-0 f-14' }}>
                                        Tools this partner currently has access to.
                                    </P>
                                </CardHeader>
                                <CardBody>
                                    <TableBuilder
                                        subHeader={true}
                                        columnsToSearch={['name']}
                                        data={subscriptionData}
                                        processing={false}
                                        title=''
                                        actions={<ModalButtonBuilder title='Create Subscription' className='btn-primary'
                                            formId='create-subscription-form'
                                            body={
                                                <Fragment>
                                                    <Form id={'create-subscription-form'} onSubmit={handleSubmit(handleSubscriptionCreate)}>
                                                        <RegisteredInputsBuilder
                                                            registrator={register}
                                                            registratorErrors={errors}
                                                            inputs={[
                                                                {
                                                                    label: 'Tool',
                                                                    type: 'select',
                                                                    size: 12,
                                                                    labelSize: 3,
                                                                    inputSize: 7,
                                                                    required: false,
                                                                    options: <FormOptionsBuilder options={buildOptionsFromArray(toolData, { label: 'name', value: 'tool_id' }, toolsToOmit)} />,
                                                                    name: 'toolToSubscribe',
                                                                }
                                                            ]} />
                                                    </Form>
                                                </Fragment>
                                            }
                                        />}
                                        columns={[
                                            {
                                                name: 'Tool',
                                                selector: (row) => row.tool_name,
                                                sortable: true,
                                                center: true,
                                            },
                                            {
                                                name: 'Subscribed At',
                                                selector: (row) => moment(new Date(row.subscribed_at * 1000)).format('YYYY-MM-DD'),
                                                sortable: true,
                                                center: true,
                                            },
                                            {
                                                name: 'Cancel Subscription',
                                                selector: (row) => <ModalButtonBuilder title='Cancel' className='btn-danger m-1'
                                                    body={
                                                        <Form id='cancel-form' onSubmit={handleSubmit(() => {
                                                            ApiRequestBuilder('toolsToolIdSubscriptionsSubscriptionIdDelete', { toolId: row.tool_id, subscriptionId: row.subscription_id }, { partnerId })
                                                                .then(function (results) {
                                                                    getPartnerData()
                                                                }).catch((err) => { console.log('err', err) })
                                                        })
                                                        }>
                                                            <p>Are you sure you want to cancel this subscription?</p>
                                                            <br />
                                                        </Form>
                                                    }
                                                />,
                                                sortable: true,
                                                center: true,
                                            }
                                        ]}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div> : null}
        </Fragment>
    );
};
export default Partner;
